/**
 * OnboardingReadinessPage
 *
 * MEMBER-ONBOARDING-DATA-READINESS-REPORT-1 Phase 2:
 * Platform Admin only — read-only onboarding data readiness evidence.
 *
 * Checks whether onboarding data is complete, internally consistent,
 * score-ready, and safe for display/exposure.
 *
 * Access gate (client mirror of server): 'Platform Admin' role only.
 * Server guard is authoritative — client gate is UX only, not security.
 */

import { FC, useCallback, useState } from 'react';
import {
  FiSearch,
  FiAlertTriangle,
  FiCheckCircle,
  FiInfo,
  FiX,
  FiXCircle,
  FiChevronDown,
  FiChevronRight,
} from 'react-icons/fi';
import {
  getOnboardingReadiness,
  type OnboardingReadinessResponse,
  type OnboardingReadinessFlags,
  type SectionBase,
  type SectionStatus,
  type TradelinesSection,
  type TradelineDetail,
} from '../services/adminOnboardingReadinessService';

// ─── Section ordering and labels ─────────────────────────────────────────────
const SECTION_ORDER: Array<{ key: string; label: string }> = [
  { key: 'identity', label: 'Identity' },
  { key: 'cohort', label: 'Cohort' },
  { key: 'plaid', label: 'Plaid' },
  { key: 'accounts', label: 'Accounts' },
  { key: 'transactions', label: 'Transactions' },
  { key: 'crs', label: 'Credit Report (CRS)' },
  { key: 'tradelines', label: 'Tradelines' },
  { key: 'matcher', label: 'Plaid ↔ Tradeline Matcher' },
  { key: 'credit_completeness', label: 'Credit Completeness' },
  { key: 'member_completeness', label: 'Member Completeness' },
  { key: 'scoring', label: 'Scoring' },
  { key: 'score_history', label: 'Score History' },
  { key: 'exposure', label: 'Exposure' },
];

// ─── Flag labels ─────────────────────────────────────────────────────────────
const FLAG_LABELS: Record<keyof OnboardingReadinessFlags, string> = {
  unclassified_is_red: 'Unclassified Tradelines',
  matcher_silence_flag: 'Matcher Silence',
  stale_crs_flag: 'Stale Credit Report',
  stale_algo_run_flag: 'Stale Scoring Run',
  missing_credit_completeness_flag: 'Missing Credit Completeness',
  missing_member_completeness_flag: 'Missing Member Completeness',
};

// ─── Status chip ─────────────────────────────────────────────────────────────
const STATUS_STYLE: Record<SectionStatus, { bg: string; color: string; icon: FC<{ size?: number }> }> = {
  GREEN:  { bg: 'bg-green-50 border-green-200',   color: 'text-green-700', icon: FiCheckCircle },
  YELLOW: { bg: 'bg-amber-50 border-amber-200',   color: 'text-amber-700', icon: FiAlertTriangle },
  RED:    { bg: 'bg-red-50 border-red-200',       color: 'text-red-700',   icon: FiXCircle },
};

const StatusChip: FC<{ status: SectionStatus; size?: 'sm' | 'md' }> = ({ status, size = 'sm' }) => {
  const cfg = STATUS_STYLE[status];
  const Icon = cfg.icon;
  const cls = size === 'md' ? 'px-2.5 py-1 text-sm' : 'px-2 py-0.5 text-xs';
  return (
    <span className={`inline-flex items-center gap-1 font-semibold rounded border ${cfg.bg} ${cfg.color} ${cls}`}>
      <Icon size={size === 'md' ? 14 : 12} /> {status}
    </span>
  );
};

// ─── Flag badge ──────────────────────────────────────────────────────────────
const FlagBadge: FC<{ label: string; active: boolean }> = ({ label, active }) => (
  <span
    className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded border ${
      active
        ? 'bg-red-50 border-red-200 text-red-700'
        : 'bg-slate-50 border-slate-200 text-slate-400'
    }`}
    title={active ? `${label}: TRUE` : `${label}: FALSE`}
  >
    {active ? <FiAlertTriangle size={11} /> : <FiCheckCircle size={11} />}
    {label}
  </span>
);

// ─── Format a single fact value for display (used for hover title) ───────────
// F1: render null/undefined as "missing" (not "—") so hover text matches body.
function formatFactValue(v: unknown): string {
  if (v === null || v === undefined) return 'missing';
  if (typeof v === 'boolean') return v ? 'true' : 'false';
  if (Array.isArray(v)) return v.length === 0 ? '[]' : `[${v.length}]`;
  if (typeof v === 'object') {
    const entries = Object.entries(v as Record<string, unknown>);
    if (entries.length === 0) return '{}';
    return entries.map(([k, val]) => `${k}: ${val}`).join(', ');
  }
  return String(v);
}

function humanKey(k: string): string {
  return k.replace(/_/g, ' ');
}

// ─── Fact value renderer (Phase A semantic highlights) ───────────────────────
// F1: null/undefined → red "missing"
// F2: scoring.latest_scoring_readiness === PARTIAL|INSUFFICIENT → amber/bold
// F3: accounts.credit_open === 0 → amber/bold
const FactValue: FC<{ sectionKey: string; factKey: string; value: unknown }> = ({
  sectionKey,
  factKey,
  value,
}) => {
  if (value === null || value === undefined) {
    return <span className="text-red-600 font-semibold">missing</span>;
  }
  if (
    sectionKey === 'scoring' &&
    factKey === 'latest_scoring_readiness' &&
    (value === 'PARTIAL' || value === 'INSUFFICIENT')
  ) {
    return <span className="text-amber-700 font-bold">{String(value)}</span>;
  }
  if (sectionKey === 'accounts' && factKey === 'credit_open' && value === 0) {
    return <span className="text-amber-700 font-bold">0</span>;
  }
  return <span className="text-slate-700">{formatFactValue(value)}</span>;
};

// ─── Section card ────────────────────────────────────────────────────────────
const SectionCard: FC<{ sectionKey: string; title: string; section: SectionBase | undefined }> = ({ sectionKey, title, section }) => {
  if (!section) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-slate-700">{title}</h3>
          <span className="text-[10px] text-slate-400 uppercase">not present</span>
        </div>
        <p className="text-xs text-slate-400">No data returned for this section.</p>
      </div>
    );
  }
  // BUG-CRS-LATEPAY-VERIFY-01: 'details' and 'derog_summary' are rendered by
  // the dedicated tradeline expander below the grid, not as flat facts here.
  const facts = Object.entries(section).filter(
    ([k]) => k !== 'status' && k !== 'details' && k !== 'derog_summary',
  );
  // BUG-CRS-LATEPAY-VERIFY-01: on the Tradelines card, surface the derogatory
  // count right on the header so the "screaming subprime" signal is visible
  // without opening the expander (plumbing GREEN can still hide bad content).
  const derog = (section as TradelinesSection).derog_summary;
  const derogCount = derog?.derogatory_count ?? 0;
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-slate-700">{title}</h3>
        <div className="flex items-center gap-2">
          {derogCount > 0 && (
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-semibold rounded border bg-red-50 border-red-200 text-red-700"
              title={`${derogCount} derogatory tradeline${derogCount === 1 ? '' : 's'} — open "Show tradeline detail" for the breakdown`}
            >
              <FiAlertTriangle size={11} />
              {derogCount} derogatory
            </span>
          )}
          <StatusChip status={section.status} />
        </div>
      </div>
      {facts.length === 0 ? (
        <p className="text-xs text-slate-400">No facts available.</p>
      ) : (
        <dl className="space-y-1">
          {facts.map(([k, v]) => (
            <div key={k} className="flex justify-between gap-3 text-xs">
              <dt className="text-slate-400 truncate max-w-[55%]" title={k}>{humanKey(k)}</dt>
              <dd className="font-mono text-right truncate max-w-[45%]" title={formatFactValue(v)}>
                <FactValue sectionKey={sectionKey} factKey={k} value={v} />
              </dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  );
};

// ─── Tradeline detail expander ───────────────────────────────────────────────
// BUG-CRS-LATEPAY-VERIFY-01: the Tradelines card reports plumbing (are rows
// present & classified?). This expander reveals the *content* — per-tradeline
// charge-offs, collections, and 30/60/90 lates — the derogatory signal that
// explains a low VeraScore but is invisible in the category roll-up.
const fmtMoney = (n: number | null): string =>
  n === null || n === undefined ? '—' : `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const fmtNum = (n: number | null): string => (n === null || n === undefined ? '—' : String(n));
const fmtDate = (d: string | null): string => (d ? String(d).slice(0, 10) : '—');

const RatingBadge: FC<{ rating: string | null }> = ({ rating }) => {
  if (!rating) return <span className="text-slate-400">—</span>;
  const r = rating.toLowerCase();
  const bad = r === 'chargeoff' || r === 'collection' || r === 'lateover120days';
  return (
    <span
      className={`inline-block px-2 py-0.5 rounded text-[11px] font-semibold ${
        bad ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-600'
      }`}
    >
      {rating}
    </span>
  );
};

const TradelineDetailExpander: FC<{ section: TradelinesSection }> = ({ section }) => {
  const [open, setOpen] = useState(false);
  const details = (section.details ?? []) as TradelineDetail[];
  const derog = section.derog_summary;
  if (details.length === 0) return null;

  return (
    <section className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 mb-6">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between text-left"
      >
        <span className="flex items-center gap-2 text-sm font-semibold text-slate-700">
          {open ? <FiChevronDown size={16} /> : <FiChevronRight size={16} />}
          Show tradeline detail
          <span className="text-xs font-normal text-slate-400">({details.length} tradelines)</span>
        </span>
        {derog && derog.derogatory_count > 0 && (
          <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded border bg-red-50 border-red-200 text-red-700">
            <FiAlertTriangle size={12} />
            {derog.derogatory_count} derogatory
          </span>
        )}
      </button>

      {open && (
        <div className="mt-4">
          {derog && (
            <div className="flex flex-wrap gap-2 mb-3 text-xs">
              <span className="px-2 py-1 rounded bg-red-50 border border-red-200 text-red-700">
                Charge-offs: <strong>{derog.chargeoff_count}</strong>
              </span>
              <span className="px-2 py-1 rounded bg-red-50 border border-red-200 text-red-700">
                Collections: <strong>{derog.collection_count}</strong>
              </span>
              <span className="px-2 py-1 rounded bg-red-50 border border-red-200 text-red-700">
                Late &gt;120d: <strong>{derog.late_over_120_count}</strong>
              </span>
              <span className="px-2 py-1 rounded bg-amber-50 border border-amber-200 text-amber-700">
                Tradelines w/ any late: <strong>{derog.tradelines_with_any_late}</strong>
              </span>
              <span className="px-2 py-1 rounded bg-amber-50 border border-amber-200 text-amber-700">
                Total late events: <strong>{derog.total_late_events}</strong>
              </span>
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-slate-50 text-left text-[11px] text-slate-500 uppercase tracking-wider">
                  <th className="px-3 py-2">Creditor</th>
                  <th className="px-3 py-2">Type</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Rating</th>
                  <th className="px-3 py-2 text-right">30</th>
                  <th className="px-3 py-2 text-right">60</th>
                  <th className="px-3 py-2 text-right">90</th>
                  <th className="px-3 py-2 text-right">Charge-off</th>
                  <th className="px-3 py-2">Adverse</th>
                  <th className="px-3 py-2 text-right">Balance</th>
                  <th className="px-3 py-2 text-right">Limit</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {details.map((t, i) => (
                  <tr key={i} className={t.is_derogatory ? 'bg-red-50/60' : ''}>
                    <td className="px-3 py-2 font-medium text-slate-700 max-w-[180px] truncate" title={t.creditor_name ?? ''}>
                      {t.creditor_name ?? '—'}
                      {t.is_authorized_user ? <span className="ml-1 text-[10px] text-slate-400">(AU)</span> : null}
                    </td>
                    <td className="px-3 py-2 text-slate-600">{t.account_type ?? '—'}</td>
                    <td className="px-3 py-2 text-slate-600">{t.status_reported ?? '—'}</td>
                    <td className="px-3 py-2"><RatingBadge rating={t.current_rating_type} /></td>
                    <td className="px-3 py-2 text-right font-mono">{fmtNum(t.times_30_days_late)}</td>
                    <td className="px-3 py-2 text-right font-mono">{fmtNum(t.times_60_days_late)}</td>
                    <td className={`px-3 py-2 text-right font-mono ${(t.times_90_days_late ?? 0) > 0 ? 'text-red-700 font-bold' : ''}`}>
                      {fmtNum(t.times_90_days_late)}
                    </td>
                    <td className="px-3 py-2 text-right font-mono">{fmtMoney(t.chargeoff_amount)}</td>
                    <td className="px-3 py-2 text-slate-600">{fmtDate(t.most_recent_adverse_date)}</td>
                    <td className="px-3 py-2 text-right font-mono">{fmtMoney(t.balance_reported)}</td>
                    <td className="px-3 py-2 text-right font-mono">{fmtMoney(t.credit_limit)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </section>
  );
};


// ─── Overall banner ──────────────────────────────────────────────────────────
const OverallBanner: FC<{ result: OnboardingReadinessResponse }> = ({ result }) => {
  const cfg = STATUS_STYLE[result.overall_status];
  return (
    <div className={`rounded-xl border p-4 mb-6 ${cfg.bg}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <StatusChip status={result.overall_status} size="md" />
          <div>
            <div className="text-xs text-slate-500 uppercase tracking-wider">Member</div>
            <div className="font-mono text-sm text-slate-700">{result.member_id}</div>
          </div>
          <div className="border-l border-slate-300/60 pl-3">
            <div className="text-xs text-slate-500 uppercase tracking-wider">Resolved From</div>
            <div className="font-mono text-sm text-slate-700">
              {result.resolved_from.type}: {result.resolved_from.value_redacted}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div>
            <div className="text-xs text-slate-500 uppercase tracking-wider">Report Version</div>
            <div className="font-mono text-sm text-slate-700">{result.report_version}</div>
          </div>
          <div>
            <div className="text-xs text-slate-500 uppercase tracking-wider">Generated</div>
            <div className="font-mono text-xs text-slate-700">{new Date(result.generated_at).toLocaleString()}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Main page ───────────────────────────────────────────────────────────────
const OnboardingReadinessPage: FC = () => {
  const [identifier, setIdentifier] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<OnboardingReadinessResponse | null>(null);

  const handleSearch = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    const id = identifier.trim();
    if (!id) return;
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const data = await getOnboardingReadiness(id);
      setResult(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load onboarding readiness report');
    } finally {
      setLoading(false);
    }
  }, [identifier]);

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-800">Member Onboarding Readiness Report</h1>
        <p className="text-sm text-slate-500 mt-1">Read-only onboarding data readiness evidence</p>
      </div>

      {/* Search */}
      <section className="bg-white rounded-xl border border-slate-200 p-4 mb-6 shadow-sm">
        <p className="text-xs text-slate-600 mb-3">
          Checks whether onboarding data is complete, internally consistent, score-ready,
          and safe for display/exposure.
        </p>
        <form onSubmit={handleSearch} className="flex items-end gap-3">
          <div className="flex-1">
            <label className="block text-xs font-medium text-slate-600 mb-1">Member UUID, Email, or Auth0 ID</label>
            <input
              type="text"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              placeholder="UUID, email@example.com, or auth0|abc123"
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <button
            type="submit"
            disabled={loading || !identifier.trim()}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
          >
            <FiSearch size={14} />
            {loading ? 'Loading…' : 'Load Report'}
          </button>
        </form>
        <p className="mt-2 text-xs text-slate-500">
          All requests are logged with purpose <span className="font-mono">compliance_audit</span>. No raw PII is displayed.
        </p>
      </section>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 mb-6 flex items-start gap-3">
          <FiAlertTriangle className="mt-0.5 flex-shrink-0" size={18} />
          <div className="flex-1">
            <p className="text-sm font-medium">{error}</p>
          </div>
          <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600 flex-shrink-0">
            <FiX size={16} />
          </button>
        </div>
      )}

      {/* Loading */}
      {loading && !result && (
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center shadow-sm">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
          <p className="text-sm text-slate-500">Loading onboarding readiness report…</p>
        </div>
      )}

      {/* Empty */}
      {!loading && !result && !error && (
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center shadow-sm">
          <FiInfo size={28} className="mx-auto text-slate-300 mb-3" />
          <p className="text-sm font-medium text-slate-600 mb-1">Enter a member identifier above to load a report</p>
          <p className="text-xs text-slate-400">Accepts member UUID, email, or Auth0 ID.</p>
        </div>
      )}


      {/* Result */}
      {result && (
        <>
          <OverallBanner result={result} />

          {/* Flags row */}
          <section className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 mb-6">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-slate-700">Flags</h2>
              <span className="text-xs text-slate-400">
                {Object.values(result.flags).filter(Boolean).length} of {Object.keys(result.flags).length} active
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              {(Object.keys(FLAG_LABELS) as Array<keyof OnboardingReadinessFlags>).map((k) => (
                <FlagBadge key={k} label={FLAG_LABELS[k]} active={Boolean(result.flags[k])} />
              ))}
            </div>
          </section>

          {/* Section cards grid */}
          <section className="mb-6">
            <h2 className="text-sm font-semibold text-slate-700 mb-3">Sections</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {SECTION_ORDER.map(({ key, label }) => (
                <SectionCard key={key} sectionKey={key} title={label} section={result.sections[key]} />
              ))}
            </div>
          </section>

          {/* BUG-CRS-LATEPAY-VERIFY-01: per-tradeline content detail (expander) */}
          {result.sections.tradelines && (
            <TradelineDetailExpander section={result.sections.tradelines} />
          )}

          {/* Notes */}
          {result.notes && result.notes.length > 0 && (
            <section className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 mb-6">
              <h2 className="text-sm font-semibold text-slate-700 mb-2">Notes</h2>
              <ul className="list-disc list-inside space-y-1 text-xs text-slate-600">
                {result.notes.map((n, i) => (
                  <li key={i}>{n}</li>
                ))}
              </ul>
            </section>
          )}
        </>
      )}
    </div>
  );
};

export default OnboardingReadinessPage;
